import { cn } from "~/lib/utils";
import { formatCurrencyValue } from "~/utils/functions/formatCurrencyValue";

// // // //

/**
 * TooltipContent
 * Render content for chart tooltip
 */
export function TooltipContent(props: {
    value: number;
    name: string;
    label?: string;
    /** Show the asset icon alongside the name. Defaults to `false`. */
    showIcon?: boolean;
    /** Icon URL rendered when `showIcon` is true. */
    iconUrl?: string;
    /** Show a "click to see details" hint at the bottom. Defaults to `false`. */
    showClickHint?: boolean;
}) {
    const {
        value,
        name,
        label = "Net Flows",
        showIcon = false,
        iconUrl,
        showClickHint = false,
    } = props;

    // Determine styling based on value
    const isPositive = value >= 0;
    const valueColor = isPositive ? "text-emerald-500" : "text-red-500";
    const valueBackground = isPositive ? "bg-emerald-50" : "bg-red-50";

    // Format values
    const formattedValue = formatCurrencyValue(Math.abs(value));

    return (
        <div className="flex flex-col select-none p-4 min-w-[24rem]">
            <div className="flex items-center gap-2 mb-3">
                {showIcon && iconUrl && (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                        src={iconUrl}
                        alt={name}
                        className="h-5 w-5 shrink-0 rounded-full"
                    />
                )}
                <div className="flex-1">
                    <h3 className="text-sm font-semibold text-foreground leading-tight mb-0.5">
                        {name}
                    </h3>
                </div>
            </div>

            <div
                className={cn(
                    "flex items-center justify-between px-3.5 py-3 rounded-md",
                    valueBackground,
                )}
            >
                <p className={cn("text-base font-medium", valueColor)}>
                    {label}
                </p>
                <span
                    className={cn(
                        "text-base font-bold tabular-nums",
                        valueColor,
                    )}
                >
                    {isPositive ? "+" : "-"}&nbsp;${formattedValue}
                </span>
            </div>

            {showClickHint && (
                <p className="mt-4 text-center text-xs text-muted-foreground">
                    Click to see details
                </p>
            )}
        </div>
    );
}
